import { useState } from 'react'
import { SectionCard } from '../DetailBits'
import { uploadedPhotos } from '../bookingDetailsData'
import { useBookingSuggestions } from '../useBookingSuggestions'
import { loadImage } from './loadImage'

const FAIL_COPY = 'We couldn’t prepare the PDF just now. Please try again.'

/**
 * Seeker sidebar card on a completed consultation: one PDF with every
 * detail of the booking — summary, payment, both parties, the astrologer's
 * note, the suggested remedy products and the photos uploaded before the
 * session. jsPDF loads on the first click only. A failure turns the button
 * into its own retry (with a tooltip) rather than a banner.
 */
export default function DownloadReportCard({ booking }) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const { data: cachedSuggestions, refetch } = useBookingSuggestions(booking)

  const download = async () => {
    if (busy) return
    setBusy(true)
    setFailed(false)
    try {
      const photos = uploadedPhotos(booking)
      const [{ buildConsultationPdf, reportFileName }, suggestions, images] = await Promise.all([
        import('./buildConsultationPdf'),
        cachedSuggestions ? Promise.resolve(cachedSuggestions) : refetch().then((r) => r.data ?? []),
        Promise.all(photos.map((p) => loadImage(p.url))),
      ])
      const doc = buildConsultationPdf({
        booking,
        suggestions,
        photos: photos.map((p, i) => ({ ...p, image: images[i] })),
        generatedAt: new Date(),
      })
      doc.save(reportFileName(booking))
    } catch (err) {
      console.error('[consultation] report PDF failed', err)
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard title="Consultation Report" icon="📄">
      <p className="text-xs text-[#8B7355] -mt-2 mb-5 leading-relaxed">
        Download everything from this consultation as a PDF — the session summary, your details, the
        astrologer&rsquo;s advice, suggested remedies and the photos you uploaded.
      </p>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        aria-busy={busy}
        title={failed ? FAIL_COPY : 'Download the full consultation report as a PDF'}
        className="relative hero-btn-primary w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[10px] font-bold uppercase tracking-[0.18em] disabled:opacity-70 disabled:cursor-wait"
      >
        {busy ? (
          <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden="true" />
        ) : (
          <span aria-hidden="true">⬇</span>
        )}
        {busy ? 'Preparing PDF…' : failed ? 'Try Download Again' : 'Download Report'}
        {failed && !busy && (
          <span
            className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white text-[11px] leading-5 font-bold"
            aria-label={FAIL_COPY}
          >
            !
          </span>
        )}
      </button>
    </SectionCard>
  )
}
